import { useEffect, useId, useState } from "react";
import {
    Button,
    Toast,
    Toaster,
    useToastController,
    makeStyles,
    tokens,
    Tooltip,
    ToastTitle,
    Spinner
} from "@fluentui/react-components";
import {
    Delete16Regular,
    ArrowClockwise16Regular,
} from "@fluentui/react-icons";
import { formatDateTime } from "../adminUtil";
import SalesforceConnectButton from "../SalesforceConnection/SalesforceConnectButton";
import AdminService from "../AdminService";

const useStyles = makeStyles({
    wrapper: {
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        padding: "8px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "8px",
        justifyContent: "space-between",
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        backgroundColor: tokens.colorNeutralBackground1,

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },

        "& th, & td": {
            padding: "6px 6px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
            verticalAlign: "middle",
        },
    },
    actions: {
        "& > :not(:last-child)": {
            marginRight: "6px",
        },
    },
    createdHeader: {
        width: "150px",
    },
    instanceUrlCell: {
        width: "240px"
    },
    empty: {
        color: tokens.colorNeutralForeground3,
        padding: "10px",
    },
    usernameCell: {
        maxWidth: "250px",
        whiteSpace: "normal",
        wordBreak: "break-word",
    },
    actionsDiv: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-evenly"
    }
});

function toArraySf(sf) {
    if (!sf) return [];
    return Array.isArray(sf) ? sf : [];
}

export default function SfOrgsTable({ tenant, onChange }) {
    if (!tenant || !tenant.config) return null;

    const styles = useStyles();
    const [rows, setRows] = useState(() =>
        toArraySf(tenant?.config?.sf)
    );
    const [deletingOrgId, setDeletingOrgId] = useState(null);
    useEffect(() => {
        setRows(toArraySf(tenant?.config?.sf));
    }, [tenant?.config?.sf]);

    const toasterId = useId();
    const { dispatchToast } = useToastController(toasterId);

    const deleteOrg = async (orgId) => {
        const prevRows = [...rows]; // backup for rollback

        //  optimistic update
        const next = rows.filter((r) => r.orgId !== orgId);
        setRows(next);

        onChange({
            ...tenant,
            config: {
                ...tenant.config,
                sf: next,
            },
        });

        setDeletingOrgId(orgId);

        try {
            await AdminService.deleteSFConnection(orgId);

            dispatchToast(
                <Toast>
                    <ToastTitle>Deleted {orgId} successfully ✅</ToastTitle>
                </Toast>,
                { intent: "success" }
            );
        } catch (err) {
            console.error("Delete failed", err);

            //  rollback UI
            setRows(prevRows);

            onChange({
                ...tenant,
                config: {
                    ...tenant.config,
                    sf: prevRows,
                },
            });

            dispatchToast(
                <Toast>
                    <ToastTitle>Failed to delete org ❌</ToastTitle>
                </Toast>,
                { intent: "error" }
            );
        } finally {
            setDeletingOrgId(null);
        }
    };

    return (
        <div className={styles.wrapper}>
            <Toaster toasterId={toasterId} />

            <div className={styles.header}>
                <div>
                    <strong>Salesforce Orgs</strong>
                    <span className={styles.badge}>
                        {rows.length} org(s)
                    </span>
                </div>

                <SalesforceConnectButton />
            </div>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Org ID</th>
                        <th className={styles.instanceUrlCell}>Instance URL</th>
                        <th>User ID</th>
                        <th style={{ width: 250 }}>Username</th>
                        <th className={styles.createdHeader}>Created At (UTC)</th>
                        <th className={styles.createdHeader}>Updated At (UTC)</th>
                        <th style={{ width: 90 }}>Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {rows.map((r) => (
                        <tr key={r.orgId}>
                            <td>{r.orgId}</td>
                            <td>{r.instanceUrl || "-"}</td>
                            <td>{r.sfOrgOwnerUserId || "-"}</td>
                            <td className={styles.usernameCell}>
                                {r.sfOrgOwner || "-"}
                            </td>
                            <td>
                                {formatDateTime(r.createdAt)}
                            </td>
                            <td>
                                {formatDateTime(r.updatedAt)}
                            </td>

                            <td className={styles.actions}>
                                <div className={styles.actionsDiv}>
                                    <SalesforceConnectButton>
                                        <Tooltip content="Refresh Connection">
                                            <Button
                                                size="small"
                                                appearance="transparent"
                                                icon={<ArrowClockwise16Regular />}
                                            />
                                        </Tooltip>
                                    </SalesforceConnectButton>

                                    <Tooltip content="Delete">
                                        <Button
                                            size="small"
                                            appearance="transparent"
                                            icon={
                                                deletingOrgId === r.orgId ? <Spinner size="tiny" /> : <Delete16Regular />
                                            }
                                            onClick={() => deleteOrg(r.orgId)}
                                            disabled={deletingOrgId === r.orgId}
                                        />
                                    </Tooltip>
                                </div>
                            </td>
                        </tr>
                    ))}

                    {rows.length === 0 && (
                        <tr>
                            <td className={styles.empty} colSpan={6}>
                                No Salesforce orgs yet.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}